import { motion } from "framer-motion";
import { useMemo } from "react";
import type { Car, FuelActivityFilters, FuelLog, GasStation, User } from "@/types/database";
import {
  buildFuelInsights,
  buildYearlySpendInsight,
  enrichFuelLogs,
  filterEnrichedLogs,
  filterLogsByDateRange,
  getCurrentMonthFuelOverview,
  getUserFuelCharts,
  getYearlySpendBreakdown,
  previousPeriodLogs,
  type FuelInsight,
} from "@/lib/fuel-analytics";
import { exportFuelInsightsJson, exportFuelLogsCsv } from "@/lib/fuel-export";
import { UserFuelCharts } from "@/components/fuel-activity/FuelActivityCharts";
import { FuelActivityFiltersBar } from "@/components/fuel-activity/FuelActivityFiltersBar";
import { FuelCurrentMonthMetrics } from "@/components/fuel-activity/FuelCurrentMonthMetrics";
import { FuelInsightsPanel } from "@/components/fuel-activity/FuelInsightsPanel";
import { FuelLogsTable } from "@/components/fuel-activity/FuelLogsTable";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";
import { carTitle, userDisplayName } from "@/lib/utils";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

type Props = {
  user: User | null;
  userID: string;
  logs: FuelLog[];
  cars: Car[];
  stations: GasStation[];
  filters: FuelActivityFilters;
  onFiltersChange: (next: FuelActivityFilters) => void;
};

export function UserFuelActivityAnalytics({
  user,
  userID,
  logs,
  cars,
  stations,
  filters,
  onFiltersChange,
}: Props) {
  const userLabel = user ? userDisplayName(user) : userID.slice(0, 8);

  const periodLogs = useMemo(
    () => filterLogsByDateRange(logs, filters.dateRange),
    [logs, filters.dateRange]
  );

  const currentMonth = useMemo(() => getCurrentMonthFuelOverview(logs), [logs]);
  const charts = useMemo(() => getUserFuelCharts(periodLogs, cars, stations), [periodLogs, cars, stations]);
  const previous = useMemo(
    () => previousPeriodLogs(logs, filters.dateRange),
    [logs, filters.dateRange]
  );
  const insights = useMemo(
    () => buildFuelInsights(periodLogs, previous, userLabel),
    [periodLogs, previous, userLabel]
  );

  const yearlyBreakdown = useMemo(() => {
    const userList = user ? [user] : [];
    return getYearlySpendBreakdown(logs, userList, cars, stations, new Date().getFullYear());
  }, [logs, user, cars, stations]);

  const yearlySpendInsight = useMemo(
    () => buildYearlySpendInsight(yearlyBreakdown, { userLabel, userID }),
    [yearlyBreakdown, userLabel, userID]
  );

  const allInsights = useMemo(() => {
    const items: FuelInsight[] = [];
    if (yearlySpendInsight) items.push(yearlySpendInsight);
    items.push(...insights);
    return items;
  }, [yearlySpendInsight, insights]);

  const enrichedLogs = useMemo(() => {
    const userList = user ? [user] : [];
    const enriched = enrichFuelLogs(periodLogs, userList, cars, stations);
    return filterEnrichedLogs(enriched, filters);
  }, [periodLogs, user, cars, stations, filters]);

  const gradeOptions = useMemo(
    () => [...new Set(periodLogs.map((log) => log.fuelGrade))].sort(),
    [periodLogs]
  );

  const carOptions = useMemo(() => {
    const ids = [...new Set(periodLogs.map((log) => log.carID))];
    return ids.map((id) => {
      const car = cars.find((entry) => entry.carID === id);
      return { id, label: car ? carTitle(car) : id.slice(0, 8) };
    });
  }, [periodLogs, cars]);

  const stationOptions = useMemo(() => {
    const ids = [...new Set(periodLogs.map((log) => log.gasStationID))];
    return ids.map((id) => {
      const station = stations.find((entry) => entry.stationID === id);
      return { id, label: station?.name ?? id.slice(0, 8) };
    });
  }, [periodLogs, stations]);

  return (
    <div className="space-y-6">
      <Workspace>
        <WorkspaceSection className="border-b border-border/30 p-4 sm:p-5">
          <FuelActivityFiltersBar
            filters={filters}
            onChange={onFiltersChange}
            showSort={false}
            showGrade
            showCar
            showStation
            gradeOptions={gradeOptions}
            carOptions={carOptions}
            stationOptions={stationOptions}
            onExport={() => exportFuelLogsCsv(enrichedLogs, `fuel-logs-${userID}.csv`)}
            exportLabel="Export logs CSV"
          />
        </WorkspaceSection>
      </Workspace>

      <motion.div variants={containerVariants} initial="hidden" animate="show">
        <FuelCurrentMonthMetrics
          monthLabel={currentMonth.label}
          overview={currentMonth.overview}
          spendLink={`/fuel-activity/users/${userID}/spend`}
        />
      </motion.div>

      <FuelInsightsPanel insights={allInsights} />

      <UserFuelCharts charts={charts} />

      <Workspace>
        <WorkspaceSection className="p-4 sm:p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted">All logs</p>
              <p className="mt-0.5 text-sm text-muted">
                {enrichedLogs.length} top-up{enrichedLogs.length === 1 ? "" : "s"} in the selected period.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                exportFuelInsightsJson(
                  yearlyBreakdown.overview,
                  allInsights,
                  `fuel-insights-${userID}.json`,
                  undefined,
                  yearlyBreakdown
                )
              }
              className="rounded-full bg-canvas px-4 py-2 text-sm font-semibold transition hover:bg-canvas-hover"
            >
              Export insights JSON
            </button>
          </div>
          <FuelLogsTable logs={enrichedLogs} />
        </WorkspaceSection>
      </Workspace>
    </div>
  );
}
